import { Language } from "@app/provider";
import { TokenizedLinkAction } from "./useTokenizedLinks";
import { useLazyQuery } from "@apollo/client";
import TokenizedLinksService from "@app/service/tokenizedLinks";
import { useNavigate } from "react-router-dom";
import { useState } from "react";
import { routes } from "@app/routing";

export type NotificationTemplateName =
  | PreBillNotificationTemplate
  | UPSNotificationTemplate
  | PostBillNotificationTemplate
  | AccountNotificationTemplate;

export enum PreBillNotificationTemplate {
  PRE_BILL_COMM = "PRE_BILL_COMM",
  PRE_BILL_REMINDER = "PRE_BILL_REMINDER",
}

export enum AccountNotificationTemplate {
  ACTIVATE_YOUR_ACCOUNT = "ACTIVATE_YOUR_ACCOUNT",
}

export enum PostBillNotificationTemplate {
  POST_BILL_COMM = "Post Bill Comm",
  POSTBILL_PUBLIC_PAYMENT_SUCCESS = "POSTBILL_PUBLIC_PAYMENT_SUCCESS",
  POSTBILL_TOKENIZED_PAYMENT_SUCCESS = "POSTBILL_TOKENIZED_PAYMENT_SUCCESS",
}

export enum UPSNotificationTemplate {
  UPS_COMM = "UPS Bill Comm",
}

export type GeneratedTokenizedLinkData =
  | SignUpTokenizedLinkData
  | ScheduleDrawTokenizedLinkData
  | PostBillGuestLoginTokenizedLinkData
  | PreBillGuestLoginTokenizedLinkData;

export type BaseTokenizedLinkData = {
  language: Language;
  notificationType?: NotificationTemplateName;
};

export type SignUpTokenizedLinkData = BaseTokenizedLinkData & {
  patientUid: string;
};

export type ScheduleDrawTokenizedLinkData = BaseTokenizedLinkData & {
  orderUidPublic: string;
};

export type PostBillGuestLoginTokenizedLinkData = BaseTokenizedLinkData & {
  orderUidPublic: string;
  patientUid: string;
  lineItemUid: string;
};

export type PreBillGuestLoginTokenizedLinkData = BaseTokenizedLinkData & {
  orderUidPublic: string;
};

export type GeneratedTokenizedLink = {
  action: TokenizedLinkAction;
  data: GeneratedTokenizedLinkData;
  shortened: boolean;
};

export type GetGeneratedTokenizedLinkPayload = {
  link: string;
};

type GeneratedTokenizedLinksController = {
  isLoading: boolean;
  getTokenizedLink: (
    getGeneratedTokenizedLink: GeneratedTokenizedLink
  ) => Promise<GetGeneratedTokenizedLinkPayload | undefined>;
};

type UseGeneratedTokenizedLink = () => GeneratedTokenizedLinksController;

const useGeneratedTokenizedLink: UseGeneratedTokenizedLink = () => {
  const navigate = useNavigate();
  const [isLoading, setLoading] = useState<boolean>(false);

  const [getGenratedTokenizedLink] = useLazyQuery<{
    getGeneratedTokenizedLink: GetGeneratedTokenizedLinkPayload;
  }>(TokenizedLinksService.getGeneratedTokenizedLink());

  const getTokenizedLink = async (
    generatedTokenizedLink: GeneratedTokenizedLink
  ) => {
    setLoading(true);

    const response = await getGenratedTokenizedLink({
      variables: {
        generatedTokenizedLink,
      },
    });
    const payload = response.data?.getGeneratedTokenizedLink;
    if (!payload) {
      navigate(routes.home, { replace: true });
      return;
    }

    return payload;
  };

  return {
    isLoading,
    getTokenizedLink,
  };
};

export default useGeneratedTokenizedLink;
